// Print a single inventory movement (adjust / in / out) on A4.
// Mirrors the visual conventions of printPurchase.js.
import { API_BASE } from "@/lib/api";

const resolveImg = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return API_BASE.replace(/\/api$/, "") + u;
};

const escape = (s) => {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
};

export function printStockMovement(mov, product, template, branchName = "", lang = "ar") {
  const isAR = lang === "ar";
  const dir = isAR ? "rtl" : "ltr";
  const t = (en, ar) => (isAR ? ar : en);
  const primary = template?.primary_color || "#D4AF64";

  const dateStr = (() => {
    try { return new Date(mov.created_at).toLocaleString(isAR ? "ar-EG" : "en-US"); }
    catch { return mov.created_at || ""; }
  })();

  const kindLabel = (k) => {
    if (k === "in") return t("Stock In", "إدخال مخزون");
    if (k === "out") return t("Stock Out", "إخراج مخزون");
    return t("Adjustment", "تسوية مخزون");
  };
  const kindColor = (k) => (k === "in" ? "#16a34a" : k === "out" ? "#dc2626" : primary);

  const prodName = product ? (isAR ? (product.name_ar || product.name_en) : (product.name_en || product.name_ar)) : mov.product_id;
  const qty = Number(mov.qty_delta || 0);
  const qtySign = qty > 0 ? "+" : "";

  const html = `
<!DOCTYPE html>
<html lang="${isAR ? "ar" : "en"}" dir="${dir}">
<head>
<meta charset="UTF-8" />
<title>${escape(mov.movement_id || "")}</title>
<style>
  @page { size: A4; margin: 14mm; }
  * { box-sizing: border-box; }
  html, body {
    margin: 0; padding: 0;
    font-family: ${isAR ? "'IBM Plex Sans Arabic', sans-serif" : "'Inter', -apple-system, sans-serif"};
    color: #111; background: #fff; font-size: 11pt;
  }
  .page { width: 100%; max-width: 182mm; margin: 0 auto; padding: 8mm 0; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; padding-bottom: 12px; border-bottom: 2px solid ${primary}; }
  .brand { display: flex; gap: 12px; align-items: center; }
  .logo { width: 70px; height: 70px; object-fit: contain; }
  .biz h1 { margin: 0 0 4px 0; font-size: 20pt; color: ${primary}; }
  .biz .meta { font-size: 9pt; color: #555; line-height: 1.5; }
  .doc-info { text-align: ${isAR ? "left" : "right"}; }
  .doc-info .doc-title { font-size: 16pt; color: ${primary}; font-weight: 700; margin-bottom: 6px; }
  .doc-info .row { font-size: 10pt; color: #333; line-height: 1.6; }
  .doc-info .label { color: #777; margin-${isAR ? "left" : "right"}: 6px; }

  .info-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; margin-top: 16px; }
  .info-box { border: 1px solid #ddd; padding: 10px 12px; border-radius: 4px; background: #fafafa; }
  .info-box h3 { margin: 0 0 6px 0; font-size: 9pt; color: #666; text-transform: uppercase; letter-spacing: 0.5px; }
  .info-box .v { font-size: 11pt; color: #111; line-height: 1.5; }

  .qty-card { margin-top: 22px; padding: 22px; border: 2px solid ${primary}; border-radius: 8px; background: #fffaf0; display: flex; justify-content: space-between; align-items: center; }
  .qty-card .left { font-size: 11pt; color: #555; }
  .qty-card .product { font-size: 16pt; color: #111; font-weight: 700; margin-top: 4px; }
  .qty-card .sku { font-size: 9.5pt; color: #888; margin-top: 2px; }
  .qty-card .right { text-align: center; }
  .qty-card .kind-badge { display: inline-block; padding: 4px 12px; border-radius: 999px; background: ${kindColor(mov.kind)}; color: #fff; font-size: 10pt; font-weight: 600; margin-bottom: 8px; }
  .qty-card .qty { font-size: 30pt; font-weight: 700; color: ${kindColor(mov.kind)}; line-height: 1; font-variant-numeric: tabular-nums; }

  .reason-box { margin-top: 22px; padding: 14px 16px; background: #fafafa; border: 1px dashed #ccc; border-radius: 4px; }
  .reason-box h3 { margin: 0 0 6px 0; font-size: 9pt; color: #666; text-transform: uppercase; letter-spacing: 0.5px; }
  .reason-box .v { font-size: 12pt; color: #111; white-space: pre-line; }

  .sig-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-top: 40px; }
  .sig-box { border-top: 1px solid #888; padding-top: 6px; font-size: 10pt; color: #444; text-align: center; }

  .footer { margin-top: 28px; padding-top: 14px; border-top: 1px dashed #aaa; font-size: 9pt; color: #888; text-align: center; }

  .actions { margin-top: 12px; text-align: center; padding-top: 14px; }
  .actions button { background: ${primary}; color: #fff; border: 0; padding: 10px 20px; border-radius: 6px; cursor: pointer; font-size: 11pt; }

  @media print {
    .actions { display: none; }
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
</style>
</head>
<body>
  <div class="page">
    <div class="header">
      <div class="brand">
        ${template?.logo_url ? `<img class="logo" src="${escape(resolveImg(template.logo_url))}" alt="" />` : ""}
        <div class="biz">
          <h1>${escape(isAR ? template?.business_name_ar : template?.business_name_en) || ""}</h1>
          <div class="meta">
            ${template?.phone ? `<div>${t("Phone", "هاتف")}: ${escape(template.phone)}</div>` : ""}
            ${template?.email ? `<div>${escape(template.email)}</div>` : ""}
            ${template?.tax_number ? `<div>${t("VAT", "الرقم الضريبي")}: ${escape(template.tax_number)}</div>` : ""}
          </div>
        </div>
      </div>
      <div class="doc-info">
        <div class="doc-title">${t("Stock Movement", "حركة مخزون")}</div>
        <div class="row"><span class="label">${t("Ref #", "رقم المرجع")}:</span> <strong>${escape((mov.movement_id || "").slice(0, 14))}</strong></div>
        <div class="row"><span class="label">${t("Date", "التاريخ")}:</span> ${escape(dateStr)}</div>
        ${branchName ? `<div class="row"><span class="label">${t("Branch", "الفرع")}:</span> ${escape(branchName)}</div>` : ""}
      </div>
    </div>

    <div class="info-grid">
      <div class="info-box">
        <h3>${t("Type", "النوع")}</h3>
        <div class="v">${kindLabel(mov.kind)}</div>
      </div>
      <div class="info-box">
        <h3>${t("Branch", "الفرع")}</h3>
        <div class="v">${escape(branchName || "—")}</div>
      </div>
      <div class="info-box">
        <h3>${t("Performed By", "بواسطة")}</h3>
        <div class="v">${escape(mov.created_by_name || mov.created_by || "—")}</div>
      </div>
    </div>

    <div class="qty-card">
      <div class="left">
        <div style="font-size:9pt;color:#888;text-transform:uppercase;letter-spacing:.5px;">${t("Product", "المنتج")}</div>
        <div class="product">${escape(prodName || "—")}</div>
        ${product?.sku ? `<div class="sku">${t("SKU", "الرمز")}: ${escape(product.sku)}</div>` : ""}
      </div>
      <div class="right">
        <div class="kind-badge">${kindLabel(mov.kind)}</div>
        <div class="qty">${qtySign}${qty}</div>
      </div>
    </div>

    ${mov.reason ? `<div class="reason-box"><h3>${t("Reason", "السبب")}</h3><div class="v">${escape(mov.reason)}</div></div>` : ""}

    <div class="sig-grid">
      <div class="sig-box">${t("Performed by", "نفّذها")}</div>
      <div class="sig-box">${t("Approved by", "اعتمدها")}</div>
    </div>

    <div class="footer">
      ${t("This is an internal inventory document.", "هذه وثيقة مخزون داخلية.")}
    </div>

    <div class="actions">
      <button onclick="window.print()">${t("Print", "طباعة")}</button>
    </div>
  </div>
  <script>setTimeout(function(){ window.print(); }, 400);</script>
</body>
</html>
  `;

  const w = window.open("", "_blank", "width=900,height=1200");
  if (!w) return;
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  w.location.href = url;
  setTimeout(() => { try { URL.revokeObjectURL(url); } catch (e) { /* noop */ } }, 60_000);
}
